/**
 * Reading a product's LIMS/LIS results for display.
 *
 * The calculation itself lives on the SERVER
 * (`cgt-backend/src/products/lims-calculations.ts`): these are dosing figures,
 * and a second implementation here would be free to disagree with the one
 * that is stored. This module only presents what the server recorded.
 *
 * Pure and dependency-free so it can be unit-tested — `vitest.config.ts` only
 * picks up `.test.ts` under `src`, in a node environment with no DOM, so
 * anything worth asserting has to live outside the component.
 */

/** Every result the backend records, in the order a reviewer reads them. */
export const LIMS_RESULT_ORDER = [
  'tnc_per_ml',
  'tnc_total',
  'tnc_per_kg',
  'cd34_per_mcl',
  'cd34_pct',
  'cd34_total_direct',
  'cd34_total_sp',
  'cd34_total_dp',
  'cd34_per_kg_sp',
  'cd34_per_kg_dp',
  'cd3_per_mcl',
  'cd3_pct',
  'cd3_total_direct',
  'cd3_total_sp',
  'cd3_total_dp',
  'cd3_per_kg_sp',
  'cd3_per_kg_dp',
  // Pooling (z8nrz7e32b). A split carries none of these, so they are simply
  // absent on an aliquot and `limsRowsOf` omits them.
  'cd45_viability_pct',
  'hematocrit_pct',
  'rbc_volume_ml',
  'dmso_pct',
  'initial_volume_ml',
  'buffer_ml',
] as const

export type LimsResultKey = (typeof LIMS_RESULT_ORDER)[number]

/** Mirrors `LIMS_RESULT_LABELS` on the backend. Keys never change; labels may. */
export const LIMS_RESULT_LABELS: Record<LimsResultKey, string> = {
  tnc_per_ml: 'TNC/mL (×10⁶)',
  tnc_total: 'TNC (×10⁶)',
  tnc_per_kg: 'TNC/kg (×10⁸)',
  cd34_per_mcl: 'CD34/mcL (×10³)',
  cd34_pct: 'CD34 (%)',
  cd34_total_sp: 'Total CD34 — single platform (×10⁶)',
  cd34_total_dp: 'Total CD34 — dual platform (×10⁶)',
  cd34_per_kg_sp: 'CD34/kg — single platform (×10⁶)',
  cd34_per_kg_dp: 'CD34/kg — dual platform (×10⁶)',
  cd3_per_mcl: 'CD3/mcL',
  cd3_pct: 'CD3 (%)',
  cd3_total_sp: 'Total CD3 — single platform (×10⁸)',
  cd3_total_dp: 'Total CD3 — dual platform (×10⁸)',
  cd3_per_kg_sp: 'CD3/kg — single platform (×10⁸)',
  cd3_per_kg_dp: 'CD3/kg — dual platform (×10⁸)',
  cd34_total_direct: 'Total CD34 — entered directly (×10⁶)',
  cd3_total_direct: 'Total CD3 — entered directly (×10⁸)',
  cd45_viability_pct: 'CD45 viability (%)',
  hematocrit_pct: 'Hematocrit (%)',
  rbc_volume_ml: 'RBC volume (mL)',
  dmso_pct: 'DMSO (%)',
  initial_volume_ml: 'Initial volume, buffer excluded (mL)',
  buffer_ml: 'Buffer added (mL)',
}

/** Which group a result belongs to, for the section headings. */
export const LIMS_RESULT_GROUPS: { title: string; keys: LimsResultKey[] }[] = [
  { title: 'Total nucleated cells', keys: ['tnc_per_ml', 'tnc_total', 'tnc_per_kg'] },
  {
    title: 'CD34',
    keys: [
      'cd34_per_mcl',
      'cd34_pct',
      'cd34_total_direct',
      'cd34_total_sp',
      'cd34_total_dp',
      'cd34_per_kg_sp',
      'cd34_per_kg_dp',
    ],
  },
  {
    title: 'CD3',
    keys: [
      'cd3_per_mcl',
      'cd3_pct',
      'cd3_total_direct',
      'cd3_total_sp',
      'cd3_total_dp',
      'cd3_per_kg_sp',
      'cd3_per_kg_dp',
    ],
  },
  // Only a pooled product carries these; `limsRowsOf` omits the whole group
  // for anything else, so the section simply does not appear on an aliquot.
  {
    title: 'Composition',
    keys: ['cd45_viability_pct', 'hematocrit_pct', 'rbc_volume_ml', 'dmso_pct'],
  },
  { title: 'Volumes', keys: ['initial_volume_ml', 'buffer_ml'] },
]

export interface LimsResultRow {
  key: LimsResultKey
  label: string
  value: number
}

/**
 * The results a product carries, as rows.
 *
 * Absent results are OMITTED rather than rendered as a dash or a zero. "Not
 * measured" and "measured as zero" are different facts, and a dose column is
 * the last place to blur them.
 */
export function limsRowsOf(stored: unknown, keys: readonly LimsResultKey[]): LimsResultRow[] {
  if (!stored || typeof stored !== 'object' || Array.isArray(stored)) return []
  const source = stored as Record<string, unknown>
  const rows: LimsResultRow[] = []
  for (const key of keys) {
    const raw = source[key]
    const value = typeof raw === 'number' ? raw : typeof raw === 'string' ? Number(raw) : NaN
    if (Number.isFinite(value)) rows.push({ key, label: LIMS_RESULT_LABELS[key], value })
  }
  return rows
}

/** True when a product has any recorded result at all. */
export function hasLimsResults(stored: unknown): boolean {
  return limsRowsOf(stored, LIMS_RESULT_ORDER).length > 0
}

/**
 * A result rendered for reading.
 *
 * Small numbers keep their significant figures — a CD3/kg figure is around
 * 0.0000873, and rounding that to two decimals would print "0.00" for a real
 * dose. Large ones are trimmed, because fourteen decimal places of float noise
 * is not precision.
 */
export function formatLimsValue(value: number): string {
  if (!Number.isFinite(value)) return '—'
  if (value === 0) return '0'
  const magnitude = Math.abs(value)
  if (magnitude >= 1000) return value.toFixed(1)
  if (magnitude >= 1) return value.toFixed(4).replace(/\.?0+$/, '')
  // Below 1, keep four significant figures however small it gets.
  return Number(value.toPrecision(4)).toString()
}

/**
 * Where a result set came from.
 *
 * `mock` is stamped by the stand-in LIMS source until the real integration
 * exists. Saying so on screen is the point: a reviewer must be able to tell a
 * synthetic dose from a measured one, and nothing else about the number does.
 */
export function limsSourceOf(stored: unknown): string | null {
  if (!stored || typeof stored !== 'object' || Array.isArray(stored)) return null
  const source = (stored as Record<string, unknown>).source
  return typeof source === 'string' ? source : null
}

/** The product these results were derived from, when they came from a split. */
export function limsDerivedFrom(stored: unknown): string | null {
  if (!stored || typeof stored !== 'object' || Array.isArray(stored)) return null
  const from = (stored as Record<string, unknown>).derivedFromProductId
  return typeof from === 'string' ? from : null
}


/** The results that divide by recipient weight, and so need one on file. */
export const PER_KG_KEYS: LimsResultKey[] = [
  'tnc_per_kg',
  'cd34_per_kg_sp',
  'cd34_per_kg_dp',
  'cd3_per_kg_sp',
  'cd3_per_kg_dp',
]

/**
 * True when the product has results but NO per-kg dose among them.
 *
 * Absent results are omitted from the table, which is right — "not measured"
 * and "measured as zero" are different facts. But omitting the entire dose
 * column silently reads as the feature being broken rather than as a missing
 * input, so the one case worth explaining is called out here: everything
 * computed except the doses, because no recipient weight is recorded.
 */
export function isMissingPerKgOnly(stored: unknown): boolean {
  if (!hasLimsResults(stored)) return false
  return limsRowsOf(stored, PER_KG_KEYS).length === 0
}
